import React from 'react';
import { motion } from 'framer-motion';
import { Target, Layers, Cog, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const pillars = [
  {
    id: 'strategy',
    icon: Target,
    title: 'AI Strategy',
    subtitle: 'Design enterprise AI adoption frameworks',
    color: 'teal',
    items: [
      { label: 'AI Roadmaps', description: 'Multi-year transformation plans aligned with business objectives and measurable outcomes' },
      { label: 'AI Investment Planning', description: 'ROI-focused prioritization and budget allocation frameworks for enterprise AI portfolios' },
      { label: 'AI Governance Models', description: 'Ethical frameworks, compliance structures, and responsible AI policy development' },
    ],
  },
  {
    id: 'architecture',
    icon: Layers,
    title: 'AI Architecture',
    subtitle: 'Design scalable AI platforms',
    color: 'blue',
    items: [
      { label: 'LLM Integration', description: 'Enterprise-grade large language model deployments with security and scale requirements' },
      { label: 'AI Orchestration', description: 'Multi-model coordination, workflow management, and agent orchestration frameworks' },
      { label: 'AI Data Pipelines', description: 'Real-time data flows optimized for AI workloads and model training requirements' },
    ],
  },
  {
    id: 'enablement',
    icon: Cog,
    title: 'AI Enablement',
    subtitle: 'Operationalize AI across organizations',
    color: 'orange',
    items: [
      { label: 'AI Experimentation', description: 'Sandbox environments and prototyping platforms for rapid AI solution validation' },
      { label: 'Internal AI Tooling', description: 'Custom enterprise tools and platforms that accelerate organization-wide AI adoption' },
      { label: 'AI-Enabled Workflows', description: 'Intelligent automation of business processes integrating AI into daily operations' },
    ],
  },
];

const colorClasses = {
  teal: {
    bg: 'bg-teal/10',
    text: 'text-teal',
    border: 'border-teal/30',
    glow: 'hover:shadow-[0_0_30px_rgba(14,116,144,0.2)]',
  },
  blue: {
    bg: 'bg-blue/10',
    text: 'text-blue',
    border: 'border-blue/30',
    glow: 'hover:shadow-[0_0_30px_rgba(59,130,246,0.2)]',
  },
  orange: {
    bg: 'bg-orange/10',
    text: 'text-orange',
    border: 'border-orange/30',
    glow: 'hover:shadow-[0_0_30px_rgba(249,115,22,0.2)]',
  },
};

export function AILeadershipSection() {
  return (
    <section
      id="ai-leadership"
      data-testid="ai-leadership-section"
      className="py-20 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-teal font-mono text-sm uppercase tracking-widest mb-4">
            Since 2020
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            AI Leadership Focus
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Seth's pivot toward AI transformation leadership spans three critical pillars 
            that enable organizations to successfully adopt and scale artificial intelligence.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal via-blue to-orange mx-auto rounded-full mt-8" />
        </motion.div>

        {/* Three Pillars */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, index) => {
            const colors = colorClasses[pillar.color];
            const Icon = pillar.icon;

            return (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Card
                  data-testid={`pillar-${pillar.id}`}
                  className={`glass-light border-0 h-full transition-all duration-500 ${colors.glow}`}
                >
                  <CardHeader className="pb-4">
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-4`}>
                      <Icon className={`w-7 h-7 ${colors.text}`} strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-heading text-2xl mb-2">
                      {pillar.title}
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      {pillar.subtitle}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {pillar.items.map((item, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.15 + idx * 0.1 }}
                          className="group"
                        >
                          <div className="flex items-start gap-3">
                            <ArrowRight 
                              className={`w-4 h-4 mt-1 ${colors.text} opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all`} 
                            />
                            <div>
                              <h4 className="font-semibold text-foreground mb-0.5">
                                {item.label}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
